import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiCalendar, FiX } from "react-icons/fi";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { uploadImageApi } from "../../api/attendance.api";
import {
  getCustomerByIdApi,
  getCustomerTypeListApi,
  getJobListApi,
  createCustomerPersonalApi,
  createCustomerBusinessApi,
  createCustomerUpdateRequestApi,
} from "../../api/finance.customer.api";
import { getProjectListApi } from "../../api/finance.project.api";
import { buildCustomerAddress } from "../../utils/financeFormat";
import { resolveImageUrl } from "../../utils/format";
import { extractErrorMessage } from "../../api/client";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const BORDER = "#DCE1EA";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const GRAD = "linear(to-r, #68A2FF, #2168DA)";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GENDER_TYPE = [{ id: "1", name: "Nam" }, { id: "2", name: "Nữ" }, { id: "3", name: "Khác" }];
const IDENTITY_TYPE = [{ id: "1", name: "Số CCCD/CMND" }, { id: "2", name: "Số hộ chiếu" }];
const PLACE_OF_ISSUE = [
  { id: "1", name: "Cục Cảnh sát quản lý hành chính về trật tự xã hội" },
  { id: "2", name: "Cục Cảnh sát đăng ký quản lý cư trú và dữ liệu Quốc gia về dân cư" },
  { id: "3", name: "Bộ công an" },
];

/* ---------------------------------------------------------------- field bits */
const Label = ({ children, required }) => (
  <Text fontSize="13px" fontWeight={600} mb="6px" color={TEXT}>{children}{required && <Text as="span" color="#EF2E14"> *</Text>}</Text>
);
const ErrText = ({ error }) => (error ? <Text fontSize="12px" color="red.500" mt="4px">{error}</Text> : null);

const TextField = ({ label, required, value, onChange, placeholder, uppercase, error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Input value={value || ""} onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)} placeholder={placeholder} h="46px" fontSize="14px" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" />
    <ErrText error={error} />
  </Box>
);

const SelectField = ({ label, required, value, placeholder, onOpen, error }) => (
  <Box mb="16px">
    <Label required={required}>{label}</Label>
    <Flex as="button" type="button" onClick={onOpen} align="center" justify="space-between" minH="46px" py="8px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
      <Text fontSize="14px" color={value ? TEXT : MUTED} noOfLines={2} textAlign="left">{value || placeholder || "Chọn..."}</Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain", flexShrink: 0 }} />
    </Flex>
    <ErrText error={error} />
  </Box>
);

const DateField = ({ label, required, value, onChange, error }) => {
  const iso = value && moment(value, "DD/MM/YYYY", true).isValid() ? moment(value, "DD/MM/YYYY").format("YYYY-MM-DD") : "";
  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>
      <Box position="relative" w="100%">
        <Flex align="center" justify="space-between" h="46px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px">
          <Text fontSize="14px" color={value ? TEXT : MUTED}>{value || "dd/mm/yyyy"}</Text>
          <FiCalendar color={MUTED} size={18} />
        </Flex>
        <Input type="date" value={iso} onChange={(e) => onChange(e.target.value ? moment(e.target.value, "YYYY-MM-DD").format("DD/MM/YYYY") : "")} position="absolute" inset="0" w="100%" h="100%" opacity={0} p="0" cursor="pointer" />
      </Box>
      <ErrText error={error} />
    </Box>
  );
};

const MultiSelectField = ({ label, required, values, options, placeholder, onOpen, error }) => {
  const names = (values || []).map((id) => options.find((o) => (o._id || o.id) === id)?.name).filter(Boolean);
  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>
      <Flex as="button" type="button" onClick={onOpen} align="center" justify="space-between" minH="46px" py="8px" px="12px" border="1px solid" borderColor={error ? "#EF2E14" : BORDER} borderRadius="8px" w="100%">
        <Text fontSize="14px" color={names.length ? TEXT : MUTED} noOfLines={2} textAlign="left">{names.length ? names.join(", ") : (placeholder || "Chọn...")}</Text>
        <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain", flexShrink: 0 }} />
      </Flex>
      <ErrText error={error} />
    </Box>
  );
};

const ImageField = ({ label, required, path, onChange, error }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const d = await uploadImageApi(file);
      onChange(typeof d === "string" ? d : d?.filePath);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tải ảnh thất bại"));
    } finally { setUploading(false); }
  };
  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>
      {path ? (
        <Box position="relative" w="120px" h="90px">
          <Image src={resolveImageUrl(path, EMP_API_BASE_URL)} alt="" w="100%" h="100%" objectFit="cover" borderRadius="8px" border="1px solid" borderColor={BORDER} />
          <Flex as="button" type="button" onClick={() => onChange("")} position="absolute" top="-8px" right="-8px" w="22px" h="22px" borderRadius="full" bg="#EF2E14" color="white" align="center" justify="center"><FiX /></Flex>
        </Box>
      ) : (
        <Flex as="button" type="button" onClick={() => ref.current?.click()} w="120px" h="90px" border="1px dashed" borderColor={error ? "#EF2E14" : "#C9D4E5"} borderRadius="8px" align="center" justify="center" direction="column" gap="6px" bg="#F7F9FC">
          {uploading ? <Spinner size="sm" /> : <img src={appImages.icon_upload_image} alt="" width={28} height={28} style={{ objectFit: "contain", opacity: 0.7 }} />}
          <Text fontSize="12px" color={MUTED}>{uploading ? "Đang tải" : "Thêm ảnh"}</Text>
        </Flex>
      )}
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} />
      <ErrText error={error} />
    </Box>
  );
};

const MultiImageField = ({ label, required, paths, onChange, error }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const list = paths || [];
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const d = await uploadImageApi(file);
        uploaded.push(typeof d === "string" ? d : d?.filePath);
      }
      onChange([...list, ...uploaded.filter(Boolean)]);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Tải ảnh thất bại"));
    } finally { setUploading(false); }
  };
  return (
    <Box mb="16px">
      <Label required={required}>{label}</Label>
      <Flex gap="10px" wrap="wrap">
        {list.map((p, i) => (
          <Box key={`${p}-${i}`} position="relative" w="90px" h="90px">
            <Image src={resolveImageUrl(p, EMP_API_BASE_URL)} alt="" w="100%" h="100%" objectFit="cover" borderRadius="8px" border="1px solid" borderColor={BORDER} />
            <Flex as="button" type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} position="absolute" top="-8px" right="-8px" w="22px" h="22px" borderRadius="full" bg="#EF2E14" color="white" align="center" justify="center"><FiX /></Flex>
          </Box>
        ))}
        <Flex as="button" type="button" onClick={() => ref.current?.click()} w="90px" h="90px" border="1px dashed" borderColor={error ? "#EF2E14" : "#C9D4E5"} borderRadius="8px" align="center" justify="center" direction="column" gap="4px" bg="#F7F9FC">
          {uploading ? <Spinner size="sm" /> : <img src={appImages.icon_upload_image} alt="" width={24} height={24} style={{ objectFit: "contain", opacity: 0.7 }} />}
          <Text fontSize="11px" color={MUTED}>{uploading ? "Đang tải" : "Thêm ảnh"}</Text>
        </Flex>
      </Flex>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} />
      <ErrText error={error} />
    </Box>
  );
};

/* ---------------------------------------------------------------- screen */
const CustomerForm = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { kind } = useParams(); // ca-nhan | doanh-nghiep
  const [params] = useSearchParams();
  const isBusiness = kind === "doanh-nghiep";
  const editId = params.get("id");
  const isEdit = params.get("type") === "edit" && !!editId;

  const [f, setF] = useState({ interested_project: [] });
  const [errors, setErrors] = useState({});
  const [autoFill, setAutoFill] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [opts, setOpts] = useState({ types: [], jobs: [], projects: [] });

  const set = (k) => (v) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const sheet = useDisclosure();
  const [sheetKey, setSheetKey] = useState(null);

  const sheetConfig = useMemo(() => ({
    gender: { title: "Giới tính", options: GENDER_TYPE, valueKey: "gender", labelKey: "genderLabel", store: "id" },
    identityNumber: { title: "Loại giấy tờ", options: IDENTITY_TYPE, valueKey: "identityNumber", labelKey: "identityLabel", store: "id" },
    place_of_issue: { title: "Nơi cấp", options: PLACE_OF_ISSUE, valueKey: "place_of_issue", labelKey: "placeLabel", store: "name" },
    representativeIdIssuedPlace: { title: "Nơi cấp", options: PLACE_OF_ISSUE, valueKey: "representativeIdIssuedPlace", labelKey: "repPlaceLabel", store: "name" },
    customer_group: { title: "Nhóm khách hàng", options: opts.types, valueKey: "customer_group", labelKey: "groupLabel", store: "_id" },
    job: { title: "Nghề nghiệp", options: opts.jobs, valueKey: "job", labelKey: "jobLabel", store: "_id" },
    interested_project: { title: "Dự án quan tâm", options: opts.projects, valueKey: "interested_project", multi: true, store: "_id" },
  }), [opts]);

  useEffect(() => {
    Promise.all([
      getCustomerTypeListApi().catch(() => ({ items: [] })),
      isBusiness ? Promise.resolve({ items: [] }) : getJobListApi().catch(() => ({ items: [] })),
      getProjectListApi({ page: 1, limit: 999 }).catch(() => ({ items: [] })),
    ]).then(([types, jobs, projects]) => {
      setOpts({
        types: types?.items || types || [],
        jobs: jobs?.items || jobs || [],
        projects: projects?.items || projects || [],
      });
    });
  }, [isBusiness]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getCustomerByIdApi(editId)
      .then((d) => {
        const g = d?.gender != null ? String(d.gender) : "";
        setF({
          name: d?.name || "",
          phone_number: d?.phone || "",
          email: d?.email || "",
          dateOfBirth: d?.dateOfBirth ? moment(d.dateOfBirth).format("DD/MM/YYYY") : "",
          gender: g,
          genderLabel: GENDER_TYPE.find((x) => x.id === g)?.name || "",
          job: d?.jobId || "",
          jobLabel: d?.job?.name || "",
          customer_group: d?.customerTypeId || "",
          groupLabel: d?.customerType?.name || "",
          interested_project: d?.wishlist || [],
          identityNumber: d?.identityNumber != null ? String(d.identityNumber) : "",
          identityLabel: IDENTITY_TYPE.find((x) => x.id === String(d?.identityNumber))?.name || "",
          identification_number: d?.identificationNumber || "",
          place_of_issue: d?.placeOfIdentification || "",
          placeLabel: d?.placeOfIdentification || "",
          date_of_issue: d?.dateOfIdentification ? moment(d.dateOfIdentification).format("DD/MM/YYYY") : "",
          permanent_address: d?.permanentAddress || buildCustomerAddress(d) || "",
          current_address: d?.currentAddress || "",
          bankAccount: d?.bankAccount || "",
          bankName: d?.bankName || "",
          nameAccount: d?.nameAccount || "",
          idCardFrontImage: d?.idCardFrontImage || "",
          idCardBackImage: d?.idCardBackImage || "",
          vnidImage: d?.vnidImage || "",
          // business
          businessTaxCode: d?.businessTaxCode || "",
          businessRegistrationPlace: d?.businessRegistrationPlace || "",
          businessRegistrationDate: d?.businessRegistrationDate ? moment(d.businessRegistrationDate).format("DD/MM/YYYY") : "",
          businessAddress: d?.businessAddress || "",
          businessLocationAddress: d?.businessLocationAddress || "",
          businessRegistrationImage: d?.businessRegistrationImage || [],
          representativeName: d?.representativeName || "",
          representativeDateOfBirth: d?.representativeDateOfBirth ? moment(d.representativeDateOfBirth).format("DD/MM/YYYY") : "",
          representativeIdNumber: d?.representativeIdNumber || "",
          representativeIdIssuedDate: d?.representativeIdIssuedDate ? moment(d.representativeIdIssuedDate).format("DD/MM/YYYY") : "",
          representativeIdIssuedPlace: d?.representativeIdIssuedPlace || "",
          repPlaceLabel: d?.representativeIdIssuedPlace || "",
          representativePosition: d?.representativePosition || "",
          idRepresentativeCardFrontImage: d?.idRepresentativeCardFrontImage || "",
          idRepresentativeCardBackImage: d?.idRepresentativeCardBackImage || "",
          representativeVnidImage: d?.representativeVnidImage || "",
        });
      })
      .catch((e) => { toast.error(extractErrorMessage(e, "Lấy thông tin khách hàng thất bại")); navigate(-1); })
      .finally(() => setLoading(false));
  }, [editId, isEdit, navigate]);

  const openSheet = (k) => { setSheetKey(k); sheet.onOpen(); };
  const pickSingle = (o) => {
    const cfg = sheetConfig[sheetKey];
    const stored = cfg.store === "name" ? o.name : (cfg.store === "_id" ? (o._id || o.id) : o.id);
    setF((s) => ({ ...s, [cfg.valueKey]: stored, [cfg.labelKey]: o.name }));
    setErrors((e) => (e[cfg.valueKey] ? { ...e, [cfg.valueKey]: undefined } : e));
    sheet.onClose();
  };
  const toggleMulti = (o) => {
    const cfg = sheetConfig[sheetKey];
    const id = o._id || o.id;
    setF((s) => {
      const arr = s[cfg.valueKey] || [];
      return { ...s, [cfg.valueKey]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
    setErrors((e) => (e[cfg.valueKey] ? { ...e, [cfg.valueKey]: undefined } : e));
  };

  const validatePersonal = () => {
    const e = {};
    if (!f.name?.trim()) e.name = "Tên khách hàng là bắt buộc";
    if (!f.dateOfBirth || !moment(f.dateOfBirth, "DD/MM/YYYY", true).isValid()) e.dateOfBirth = "Ngày sinh không hợp lệ";
    if (f.gender === "" || f.gender == null) e.gender = "Vui lòng chọn giới tính";
    if (!f.customer_group) e.customer_group = "Vui lòng chọn nhóm khách hàng";
    if (!f.interested_project?.length) e.interested_project = "Vui lòng chọn dự án quan tâm";
    if (!f.phone_number?.trim()) e.phone_number = "Số điện thoại là bắt buộc";
    if (!f.email?.trim()) e.email = "Email là bắt buộc";
    else if (!EMAIL_RE.test(f.email.trim())) e.email = "Email không đúng định dạng";
    if (f.identityNumber === "" || f.identityNumber == null) e.identityNumber = "Vui lòng chọn loại giấy tờ";
    if (!f.identification_number?.trim()) e.identification_number = "Số CCCD/CMND là bắt buộc";
    if (!f.idCardFrontImage) e.idCardFrontImage = "Ảnh CCCD mặt trước là bắt buộc";
    if (!f.idCardBackImage) e.idCardBackImage = "Ảnh CCCD mặt sau là bắt buộc";
    if (!f.vnidImage) e.vnidImage = "Ảnh VNID là bắt buộc";
    if (!f.date_of_issue || !moment(f.date_of_issue, "DD/MM/YYYY", true).isValid()) e.date_of_issue = "Ngày cấp không hợp lệ";
    if (!f.place_of_issue) e.place_of_issue = "Vui lòng chọn nơi cấp";
    if (!f.permanent_address?.trim()) e.permanent_address = "Địa chỉ thường trú là bắt buộc";
    if (!autoFill && !f.current_address?.trim()) e.current_address = "Địa chỉ hiện tại là bắt buộc";
    if (!f.bankAccount?.trim()) e.bankAccount = "Số tài khoản là bắt buộc";
    if (!f.bankName?.trim()) e.bankName = "Tên ngân hàng là bắt buộc";
    if (!f.nameAccount?.trim()) e.nameAccount = "Chủ tài khoản là bắt buộc";
    return e;
  };

  const validateBusiness = () => {
    const e = {};
    if (!f.name?.trim()) e.name = "Tên doanh nghiệp là bắt buộc";
    if (!f.businessTaxCode?.trim()) e.businessTaxCode = "Mã số thuế là bắt buộc";
    if (!f.businessRegistrationDate || !moment(f.businessRegistrationDate, "DD/MM/YYYY", true).isValid()) e.businessRegistrationDate = "Ngày đăng ký KD không hợp lệ";
    if (!f.businessRegistrationPlace?.trim()) e.businessRegistrationPlace = "Nơi đăng ký KD là bắt buộc";
    if (!f.businessAddress?.trim()) e.businessAddress = "Địa chỉ doanh nghiệp là bắt buộc";
    if (!f.businessLocationAddress?.trim()) e.businessLocationAddress = "Địa chỉ trụ sở là bắt buộc";
    if (!f.businessRegistrationImage?.length) e.businessRegistrationImage = "Vui lòng chọn ảnh đăng ký kinh doanh";
    if (!f.phone_number?.trim()) e.phone_number = "Số điện thoại là bắt buộc";
    if (!f.email?.trim()) e.email = "Email là bắt buộc";
    else if (!EMAIL_RE.test(f.email.trim())) e.email = "Email không đúng định dạng";
    if (!f.representativeName?.trim()) e.representativeName = "Tên người đại diện là bắt buộc";
    if (!f.representativeDateOfBirth || !moment(f.representativeDateOfBirth, "DD/MM/YYYY", true).isValid()) e.representativeDateOfBirth = "Ngày sinh không hợp lệ";
    if (f.gender === "" || f.gender == null) e.gender = "Vui lòng chọn giới tính";
    if (!/^\d{12}$/.test(f.representativeIdNumber || "")) e.representativeIdNumber = "CCCD/CMND người đại diện phải là 12 chữ số";
    if (!f.representativeIdIssuedDate || !moment(f.representativeIdIssuedDate, "DD/MM/YYYY", true).isValid()) e.representativeIdIssuedDate = "Ngày cấp không hợp lệ";
    if (!f.representativeIdIssuedPlace) e.representativeIdIssuedPlace = "Vui lòng chọn nơi cấp";
    if (!f.representativePosition?.trim()) e.representativePosition = "Chức vụ người đại diện là bắt buộc";
    if (!f.idRepresentativeCardFrontImage) e.idRepresentativeCardFrontImage = "Ảnh CCCD mặt trước là bắt buộc";
    if (!f.idRepresentativeCardBackImage) e.idRepresentativeCardBackImage = "Ảnh CCCD mặt sau là bắt buộc";
    if (!f.representativeVnidImage) e.representativeVnidImage = "Ảnh VNID là bắt buộc";
    if (!f.customer_group) e.customer_group = "Vui lòng chọn nhóm khách hàng";
    if (!f.interested_project?.length) e.interested_project = "Vui lòng chọn dự án quan tâm";
    if (!f.bankAccount?.trim()) e.bankAccount = "Số tài khoản là bắt buộc";
    if (!f.bankName?.trim()) e.bankName = "Tên ngân hàng là bắt buộc";
    if (!f.nameAccount?.trim()) e.nameAccount = "Chủ tài khoản là bắt buộc";
    return e;
  };

  const buildPersonalBody = () => {
    const body = {
      name: f.name,
      phone: f.phone_number,
      email: f.email,
      identificationNumber: f.identification_number || undefined,
      dateOfIdentification: f.date_of_issue || undefined,
      placeOfIdentification: f.place_of_issue || undefined,
      permanentAddress: f.permanent_address || undefined,
      permanentAddressProvince: null, permanentAddressDistrict: null, permanentAddressWard: null,
      currentAddress: (autoFill ? f.permanent_address : f.current_address) || undefined,
      currentAddressProvince: null, currentAddressDistrict: null, currentAddressWard: null,
      jobId: f.job || undefined,
      customerTypeId: f.customer_group || undefined,
      wishlist: (f.interested_project || []).filter(Boolean),
      note: f.note || undefined,
      bankAccount: f.bankAccount || undefined,
      bankName: f.bankName || undefined,
      nameAccount: f.nameAccount || undefined,
      dateOfBirth: f.dateOfBirth || undefined,
      isBusiness: false,
      gender: f.gender === "" ? undefined : Number(f.gender),
      idCardFrontImage: f.idCardFrontImage || undefined,
      idCardBackImage: f.idCardBackImage || undefined,
      identityNumber: f.identityNumber === "" ? undefined : Number(f.identityNumber),
      vnidImage: f.vnidImage || undefined,
    };
    if (isEdit) body.customerId = String(editId);
    return body;
  };

  const buildBusinessBody = () => {
    const body = {
      name: f.name,
      phone: f.phone_number,
      email: f.email,
      customerTypeId: f.customer_group || undefined,
      wishlist: (f.interested_project || []).filter(Boolean),
      note: f.note || undefined,
      bankAccount: f.bankAccount || undefined,
      bankName: f.bankName || undefined,
      nameAccount: f.nameAccount || undefined,
      businessTaxCode: f.businessTaxCode,
      businessRegistrationPlace: f.businessRegistrationPlace,
      businessRegistrationDate: f.businessRegistrationDate || undefined,
      businessAddress: f.businessAddress || undefined,
      businessLocationAddress: f.businessLocationAddress || undefined,
      representativeName: f.representativeName,
      representativeDateOfBirth: f.representativeDateOfBirth || undefined,
      representativeIdNumber: f.representativeIdNumber,
      representativeIdIssuedDate: f.representativeIdIssuedDate,
      representativeIdIssuedPlace: f.representativeIdIssuedPlace,
      representativePosition: f.representativePosition,
      gender: f.gender === "" ? undefined : Number(f.gender),
      idRepresentativeCardFrontImage: f.idRepresentativeCardFrontImage || undefined,
      idRepresentativeCardBackImage: f.idRepresentativeCardBackImage || undefined,
      representativeVnidImage: f.representativeVnidImage || undefined,
      businessRegistrationImage: f.businessRegistrationImage?.length ? f.businessRegistrationImage : undefined,
    };
    if (isEdit) body.customerId = String(editId);
    return body;
  };

  const onSubmit = async () => {
    const e = isBusiness ? validateBusiness() : validatePersonal();
    if (Object.keys(e).length) { setErrors(e); toast.error(Object.values(e)[0]); return; }
    setSubmitting(true);
    try {
      const body = isBusiness ? buildBusinessBody() : buildPersonalBody();
      if (isEdit) {
        await createCustomerUpdateRequestApi(body);
        toast.success(isBusiness ? "Cập nhật khách hàng doanh nghiệp thành công" : "Cập nhật khách hàng cá nhân thành công");
      } else if (isBusiness) {
        await createCustomerBusinessApi(body);
        toast.success("Tạo khách hàng doanh nghiệp thành công");
      } else {
        await createCustomerPersonalApi(body);
        toast.success("Tạo khách hàng cá nhân thành công");
      }
      navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Lưu khách hàng thất bại"));
    } finally { setSubmitting(false); }
  };

  const cfgSheet = sheetKey ? sheetConfig[sheetKey] : null;

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : (
        <>
          <Box flex="1" overflowY="auto" px="16px" pt="16px" pb="16px">
            {isBusiness ? (
              <>
                <TextField label="Tên doanh nghiệp" required value={f.name} onChange={set("name")} placeholder="Nhập tên doanh nghiệp" uppercase error={errors.name} />
                <TextField label="Mã số thuế" required value={f.businessTaxCode} onChange={set("businessTaxCode")} placeholder="Nhập mã số thuế" error={errors.businessTaxCode} />
                <DateField label="Ngày đăng ký kinh doanh" required value={f.businessRegistrationDate} onChange={set("businessRegistrationDate")} error={errors.businessRegistrationDate} />
                <TextField label="Nơi đăng ký kinh doanh" required value={f.businessRegistrationPlace} onChange={set("businessRegistrationPlace")} placeholder="Nhập nơi đăng ký" error={errors.businessRegistrationPlace} />
                <TextField label="Địa chỉ doanh nghiệp" required value={f.businessAddress} onChange={set("businessAddress")} placeholder="Nhập địa chỉ doanh nghiệp" uppercase error={errors.businessAddress} />
                <TextField label="Địa chỉ trụ sở" required value={f.businessLocationAddress} onChange={set("businessLocationAddress")} placeholder="Nhập địa chỉ trụ sở" uppercase error={errors.businessLocationAddress} />
                <MultiImageField label="Ảnh đăng ký kinh doanh" required paths={f.businessRegistrationImage} onChange={set("businessRegistrationImage")} error={errors.businessRegistrationImage} />
                <TextField label="Số điện thoại doanh nghiệp" required value={f.phone_number} onChange={set("phone_number")} placeholder="Nhập số điện thoại" error={errors.phone_number} />
                <TextField label="Email doanh nghiệp" required value={f.email} onChange={set("email")} placeholder="Nhập email" error={errors.email} />
                <TextField label="Tên người đại diện" required value={f.representativeName} onChange={set("representativeName")} placeholder="Nhập tên người đại diện" uppercase error={errors.representativeName} />
                <DateField label="Ngày sinh người đại diện" required value={f.representativeDateOfBirth} onChange={set("representativeDateOfBirth")} error={errors.representativeDateOfBirth} />
                <SelectField label="Giới tính người đại diện" required value={f.genderLabel} placeholder="Chọn giới tính" onOpen={() => openSheet("gender")} error={errors.gender} />
                <TextField label="Số CCCD người đại diện" required value={f.representativeIdNumber} onChange={set("representativeIdNumber")} placeholder="Nhập số CCCD (12 chữ số)" error={errors.representativeIdNumber} />
                <DateField label="Ngày cấp" required value={f.representativeIdIssuedDate} onChange={set("representativeIdIssuedDate")} error={errors.representativeIdIssuedDate} />
                <SelectField label="Nơi cấp" required value={f.repPlaceLabel} placeholder="Chọn nơi cấp" onOpen={() => openSheet("representativeIdIssuedPlace")} error={errors.representativeIdIssuedPlace} />
                <ImageField label="Ảnh CCCD mặt trước" required path={f.idRepresentativeCardFrontImage} onChange={set("idRepresentativeCardFrontImage")} error={errors.idRepresentativeCardFrontImage} />
                <ImageField label="Ảnh CCCD mặt sau" required path={f.idRepresentativeCardBackImage} onChange={set("idRepresentativeCardBackImage")} error={errors.idRepresentativeCardBackImage} />
                <ImageField label="Ảnh VNID" required path={f.representativeVnidImage} onChange={set("representativeVnidImage")} error={errors.representativeVnidImage} />
                <TextField label="Chức vụ người đại diện" required value={f.representativePosition} onChange={set("representativePosition")} placeholder="Nhập chức vụ" error={errors.representativePosition} />
                <SelectField label="Nhóm khách hàng" required value={f.groupLabel} placeholder="Chọn nhóm khách hàng" onOpen={() => openSheet("customer_group")} error={errors.customer_group} />
                <MultiSelectField label="Dự án quan tâm" required values={f.interested_project} options={opts.projects} placeholder="Chọn dự án" onOpen={() => openSheet("interested_project")} error={errors.interested_project} />
                <TextField label="Số tài khoản ngân hàng" required value={f.bankAccount} onChange={set("bankAccount")} placeholder="Nhập số tài khoản" error={errors.bankAccount} />
                <TextField label="Tên ngân hàng" required value={f.bankName} onChange={set("bankName")} placeholder="Nhập tên ngân hàng" error={errors.bankName} />
                <TextField label="Chủ tài khoản" required value={f.nameAccount} onChange={set("nameAccount")} placeholder="Nhập chủ tài khoản" uppercase error={errors.nameAccount} />
              </>
            ) : (
              <>
                <TextField label="Họ tên khách hàng" required value={f.name} onChange={set("name")} placeholder="Nhập họ tên" uppercase error={errors.name} />
                <DateField label="Ngày sinh" required value={f.dateOfBirth} onChange={set("dateOfBirth")} error={errors.dateOfBirth} />
                <SelectField label="Giới tính" required value={f.genderLabel} placeholder="Chọn giới tính" onOpen={() => openSheet("gender")} error={errors.gender} />
                <SelectField label="Nghề nghiệp" value={f.jobLabel} placeholder="Chọn nghề nghiệp" onOpen={() => openSheet("job")} error={errors.job} />
                <SelectField label="Nhóm khách hàng" required value={f.groupLabel} placeholder="Chọn nhóm khách hàng" onOpen={() => openSheet("customer_group")} error={errors.customer_group} />
                <MultiSelectField label="Dự án quan tâm" required values={f.interested_project} options={opts.projects} placeholder="Chọn dự án" onOpen={() => openSheet("interested_project")} error={errors.interested_project} />
                <TextField label="Số điện thoại" required value={f.phone_number} onChange={set("phone_number")} placeholder="Nhập số điện thoại" error={errors.phone_number} />
                <TextField label="Email" required value={f.email} onChange={set("email")} placeholder="Nhập email" error={errors.email} />
                <SelectField label="Loại giấy tờ" required value={f.identityLabel} placeholder="Chọn loại giấy tờ" onOpen={() => openSheet("identityNumber")} error={errors.identityNumber} />
                <TextField label="Số CCCD/CMND" required value={f.identification_number} onChange={set("identification_number")} placeholder="Nhập số CCCD/CMND" error={errors.identification_number} />
                <ImageField label="Ảnh CCCD mặt trước" required path={f.idCardFrontImage} onChange={set("idCardFrontImage")} error={errors.idCardFrontImage} />
                <ImageField label="Ảnh CCCD mặt sau" required path={f.idCardBackImage} onChange={set("idCardBackImage")} error={errors.idCardBackImage} />
                <ImageField label="Ảnh VNID" required path={f.vnidImage} onChange={set("vnidImage")} error={errors.vnidImage} />
                <DateField label="Ngày cấp" required value={f.date_of_issue} onChange={set("date_of_issue")} error={errors.date_of_issue} />
                <SelectField label="Nơi cấp" required value={f.placeLabel} placeholder="Chọn nơi cấp" onOpen={() => openSheet("place_of_issue")} error={errors.place_of_issue} />
                <TextField label="Địa chỉ thường trú" required value={f.permanent_address} onChange={set("permanent_address")} placeholder="Nhập địa chỉ thường trú" uppercase error={errors.permanent_address} />
                <Flex as="button" align="center" gap="8px" mb="16px" onClick={() => setAutoFill((p) => !p)}>
                  <Flex w="20px" h="20px" borderRadius="4px" border="1.5px solid" borderColor={autoFill ? APP.primary200 : BORDER} bg={autoFill ? APP.primary200 : "white"} align="center" justify="center">
                    {autoFill && <img src={appImages.icon_check} alt="" width={12} height={12} style={{ objectFit: "contain", filter: "brightness(0) invert(1)" }} />}
                  </Flex>
                  <Text fontSize="13px" color={TEXT}>Địa chỉ hiện tại giống địa chỉ thường trú</Text>
                </Flex>
                {!autoFill && (
                  <TextField label="Địa chỉ hiện tại" required value={f.current_address} onChange={set("current_address")} placeholder="Nhập địa chỉ hiện tại" uppercase error={errors.current_address} />
                )}
                <TextField label="Số tài khoản ngân hàng" required value={f.bankAccount} onChange={set("bankAccount")} placeholder="Nhập số tài khoản" error={errors.bankAccount} />
                <TextField label="Tên ngân hàng" required value={f.bankName} onChange={set("bankName")} placeholder="Nhập tên ngân hàng" error={errors.bankName} />
                <TextField label="Chủ tài khoản" required value={f.nameAccount} onChange={set("nameAccount")} placeholder="Nhập chủ tài khoản" uppercase error={errors.nameAccount} />
              </>
            )}
          </Box>

          <Box position="sticky" bottom="0" bg="white" borderTop="1px solid #EEF1F6" px="16px" py="12px">
            <Flex gap="16px">
              <Button flex="1" h="50px" variant="outline" borderColor={APP.primary200} color={APP.primary200} borderRadius="8px" _hover={{ bg: "#EAF2FF" }} onClick={() => navigate(-1)}>Hủy</Button>
              <Button flex="1" h="50px" bgImage={GRAD} color="white" borderRadius="8px" _hover={{ bgImage: GRAD, opacity: 0.92 }} isLoading={submitting} onClick={onSubmit}>{isEdit ? "Lưu" : "Thêm"}</Button>
            </Flex>
          </Box>
        </>
      )}

      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">{cfgSheet?.title}</Text>
            <Box maxH="55vh" overflowY="auto">
              {(cfgSheet?.options || []).map((o, i, arr) => {
                const id = o._id || o.id;
                const active = cfgSheet.multi ? (f[cfgSheet.valueKey] || []).includes(id) : (f[cfgSheet.valueKey] === (cfgSheet.store === "name" ? o.name : id));
                return (
                  <Flex key={String(id) + i} as="button" w="100%" align="center" justify="space-between" py="12px" borderBottom={i === arr.length - 1 ? "none" : "1px solid #EEF1F6"} onClick={() => (cfgSheet.multi ? toggleMulti(o) : pickSingle(o))}>
                    <Text fontSize="15px" color={active ? APP.primary200 : TEXT} fontWeight={active ? 600 : 400} textAlign="left" noOfLines={2}>{o.name}</Text>
                    {active && <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain", flexShrink: 0 }} />}
                  </Flex>
                );
              })}
            </Box>
            <Flex as="button" onClick={sheet.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={APP.primary200} color="white" fontWeight={700}>{cfgSheet?.multi ? "Xong" : "Đóng"}</Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={isEdit ? "Chỉnh sửa khách hàng" : (isBusiness ? "Thêm khách hàng doanh nghiệp" : "Thêm khách hàng cá nhân")} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title={isEdit ? "Chỉnh sửa khách hàng" : (isBusiness ? "Thêm khách hàng doanh nghiệp" : "Thêm khách hàng cá nhân")} />
      {pageBody}
    </Box>
  );
};

export default CustomerForm;
